/**
 * Booking display-status rules: how a booking's internal state flags map to the
 * user-facing status a badge should show.
 *
 * Shared (not re-derived) so web and native label bookings identically. The *presentation*
 * of each status (badge colour / variant) stays platform-side; this module owns only the
 * domain mapping — priority resolution and the user-facing label. No React / Firebase imports
 * (React-Native / Metro-bundlable per the `@elara/domain` portability guard).
 */

import type { ItineraryItemEventType } from './models/schemas/itinerary-item-event-schemas.js';
import type { BookingStatus } from './models/schemas/itinerary-item-schemas.js';

/**
 * The status a booking presents to the user. Note the label remapping in
 * {@link bookingDisplayStatusLabel}: `approved` reads as "Pending" (we've accepted it but the
 * property hasn't confirmed) and `booked` reads as "Booked".
 */
export type BookingDisplayStatus =
  | 'planned'
  | 'approved'
  | 'booked'
  | 'cancellation_pending'
  | 'cancelled'
  | 'unavailable';

/** The booking-state flags a display status is derived from. All optional; absent = false. */
export interface BookingStatusFlags {
  isCancelled?: boolean;
  /** A live cancel that did not verify the segment is gone — terminal-cancel is withheld (H2). */
  isCancellationPending?: boolean;
  /** The underlying inventory (rate / seat) is no longer available. */
  isUnavailable?: boolean;
  isBooked?: boolean;
  isApproved?: boolean;
}

/**
 * Derives the booking-state flags for {@link resolveBookingDisplayStatus} from an itinerary item's
 * status booleans, in ONE place — so a status can never be partially wired again (ELARA-872: two
 * callsites omitted `isCancellationPending` and rendered a pending cancellation as "Not booked").
 * Spread the result and add the context-dependent `isUnavailable` at the callsite when applicable.
 */
export function bookingStatusFlagsOf(item: {
  isCancelled?: boolean;
  isCancellationPending?: boolean;
  isBooked?: boolean;
  isApproved?: boolean;
}): BookingStatusFlags {
  return {
    isCancelled: item.isCancelled === true,
    isCancellationPending: item.isCancellationPending === true,
    isBooked: item.isBooked === true,
    isApproved: item.isApproved === true,
  };
}

/**
 * Resolves the single display status from a booking's state flags, applying a fixed priority.
 *
 * Priority (highest first): `cancelled` → `cancellation_pending` → `booked` → `approved` → `unavailable` → `planned`.
 * A confirmed (or pending) booking outranks `unavailable`: once an item is booked, the `unavailable`
 * flag reflects the original *search* option's price recheck, which is no longer relevant — the
 * traveler has a real booking, so it must read "Booked"/"Pending", not "Unavailable". `unavailable`
 * only surfaces for a still-planned item that can no longer be booked. `planned` is the default when
 * no exceptional flag is set — callers that treat "planned" as the implicit baseline (and so render
 * no badge) can check `status === 'planned'`.
 */
export function resolveBookingDisplayStatus(flags: BookingStatusFlags): BookingDisplayStatus {
  if (flags.isCancelled) return 'cancelled';
  // A pending cancellation outranks booked/unavailable so the user sees "Cancellation processing"
  // rather than a stale "Booked" while we resolve whether the reservation is actually gone.
  if (flags.isCancellationPending) return 'cancellation_pending';
  if (flags.isBooked) return 'booked';
  if (flags.isApproved) return 'approved';
  if (flags.isUnavailable) return 'unavailable';
  return 'planned';
}

/**
 * Aggregates the booking statuses of the rooms in one hotel stay into a single status for the grouped
 * check-in event. The LEAST-progressed status wins: if any room is still planned (or unbooked), the whole
 * stay reads as `planned` even when other rooms are already booked — the stay isn't done until every room
 * is. Cancelled rooms are excluded so they don't drag a confirmed stay, unless every room is cancelled.
 * Returns undefined when no room has any booking status yet (so no badge shows).
 */
export function aggregateBookingStatus(statuses: Array<BookingStatus | undefined>): BookingStatus | undefined {
  if (statuses.every((status) => status == null)) return undefined;
  const active = statuses.filter((status) => status !== 'cancelled');
  if (active.length === 0) return 'cancelled';
  if (active.some((status) => status == null || status === 'planned')) return 'planned';
  // A room stuck mid-cancel outranks booked/approved — the stay needs attention, not a stale "Booked".
  if (active.includes('cancellation_pending')) return 'cancellation_pending';
  if (active.includes('approved')) return 'approved';
  return 'booked';
}

/**
 * Whether booking status is a non-event for this event type — places, activities, and custom items
 * don't really get "booked" through Elara, so a bare "Not booked" badge on one would just be noise.
 * Shared so web (`BaseItineraryBlock`) and mobile (`ItineraryEventRow`) suppress the identical set;
 * a place's specific subtype (`place_dining`, `place_shopping`, …) all match via the `place` prefix.
 */
export function isUnbookedNormEventType(type: ItineraryItemEventType): boolean {
  return type === 'activity' || type === 'custom' || type.startsWith('place');
}

/** Maps a display status to its user-facing label (`approved` → "Pending", `booked` → "Booked"). */
export function bookingDisplayStatusLabel(status: BookingDisplayStatus): string {
  switch (status) {
    case 'planned':
      return 'Not booked';
    case 'approved':
      return 'Pending';
    case 'booked':
      return 'Booked';
    case 'cancellation_pending':
      return 'Cancellation processing';
    case 'cancelled':
      return 'Cancelled';
    case 'unavailable':
      // "Sold out", not "Unavailable": the cards say "Sold out" on their badge, and two words for one
      // state read as two different states. Shared with mobile, which shows the same label.
      return 'Sold out';
  }
}
